import { Button, Card } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Check, ChevronRight, CircleHelp, Lightbulb, ShieldAlert, Sparkles, TriangleAlert, UserRound, XCircle } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { Dialog } from "../../components/Dialog";
import { QueryState } from "../../components/QueryState";
import { RequestStatusChip } from "../../components/Status";
import { api, ApiError, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatDate, formatDateTime } from "../../lib/format";
import { useSession } from "../../lib/session";
import { RelatedFiles } from "../assets/RelatedFiles";

type Detail = components["schemas"]["RequestDetailOut"];
type Person = components["schemas"]["PersonOut"];

const CLARIFY_REASONS = ["AUDIENCE_UNCLEAR", "OFFER_INCOMPLETE", "OBJECTIVE_UNCLEAR", "CLAIM_NEEDS_EVIDENCE",
  "PRODUCT_INFO_MISSING", "REFERENCE_MISSING", "DEADLINE_UNCLEAR", "OTHER"] as const;
const REJECT_REASONS = ["INSUFFICIENT_BRIEF", "WEAK_UNSUPPORTED_ANGLE", "UNSUPPORTED_CLAIM",
  "MISSING_PRODUCT_INFORMATION", "POOR_STRATEGIC_BASIS", "DUPLICATE_CREATIVE", "COMPLIANCE_CONCERN",
  "UNREALISTIC_DEADLINE", "NO_CLEAR_CAMPAIGN_OBJECTIVE", "NO_VALID_AUDIENCE_INSIGHT", "OTHER"] as const;

const A = { claim: "claim", reassign: "reassign", clarify: "requestClarification",
  reject: "rejectCreativeRequest", approve: "approveCreativeRequest" } as const;

/** Request Detail / Triage (core doc 04 P3) with Clarification (P4) and Rejection (P5) dialogs.
 *  Opening the request as its owner starts the review explicitly (ADR 0008 rule 3). */
export function RequestDetailPage() {
  const { displayId = "" } = useParams();
  const { t } = useTranslation();
  const me = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["request", displayId],
    queryFn: () => call<Detail>(api.GET("/app/v1/requests/{display_id}", { params: { path: { display_id: displayId } } })) });
  const started = useRef(false);
  const startReview = useMutation({
    mutationFn: () => call(api.POST("/app/v1/requests/{display_id}/start-review", { params: { path: { display_id: displayId } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["request", displayId] }),
  });
  useEffect(() => {
    if (!started.current && q.data?.allowed_actions.includes("startReview")) {
      started.current = true;
      startReview.mutate();
    }
  }, [q.data, startReview]);
  return (
    <QueryState query={q}>
      {(d) => (
        <div className="space-y-6">
          <nav aria-label={t("requests.breadcrumb")} className="flex items-center gap-1.5 text-[13px] text-ink-2">
            <Link to="/requests" className="hover:text-ink">{t("nav.requests")}</Link>
            <ChevronRight aria-hidden="true" className="size-3.5" />
            <span className="text-ink">{d.display_id}</span>
          </nav>
          <Header d={d} />
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
            <div className="min-w-0 space-y-6">
              <Brief d={d} />
              <RelatedFiles entity={d.display_id} canUpload={Boolean(me.permissions["asset.upload"]) && d.status !== "REJECTED"}
                meta={[{ label: t("assets.meta_request"), value: d.display_id }, { label: t("requests.col_product"), value: d.product.name }, { label: t("assets.meta_uploader"), value: me.name }]} />
              <Clarifications d={d} />
              {d.rejection && <RejectionCard d={d} />}
            </div>
            <Intelligence d={d} />
          </div>
        </div>
      )}
    </QueryState>
  );
}

function Header({ d }: { d: Detail }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<null | "approve" | "clarify" | "reject" | "reassign">(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["request", d.display_id] });
    void qc.invalidateQueries({ queryKey: ["requests"] });
    void qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const claim = useMutation({
    mutationFn: () => call(api.POST("/app/v1/requests/{display_id}/claim", { params: { path: { display_id: d.display_id } } })),
    onSuccess: refresh,
  });
  const has = new Set(d.allowed_actions);
  const can = { claim: has.has(A.claim), reassign: has.has(A.reassign), clarify: has.has(A.clarify),
    reject: has.has(A.reject), approve: has.has(A.approve) };
  return (
    <div className="flex flex-wrap items-start justify-between gap-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[34px] font-semibold tracking-[-0.02em] text-ink">{d.product.name}</h1>
          <RequestStatusChip status={d.status} />
        </div>
        <p className="mt-1 text-[15px] text-ink-2">
          {d.display_id} · {t(`requests.objective_${d.brief.objective.toLowerCase()}`)} · {t("requests.due_on", { date: formatDate(d.deadline, i18n.language, me.time_zone) })}
          {d.deadline_risk && <span className="ml-2 text-[var(--status-warning)]">{t("requests.deadline_risk")}</span>}
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-[14px] text-ink-2">
          <UserRound aria-hidden="true" className="size-4" />
          {d.assigned_cp ? t("requests.assigned_to", { name: d.assigned_cp.name }) : t("requests.unassigned")}
          {" · "}{t("requests.from_marketer", { name: d.marketer.name })}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {can.claim && <Button onClick={() => claim.mutate()} disabled={claim.isPending}>{t("requests.action_claim")}</Button>}
        {can.reassign && <Button variant="ghost" onClick={() => setDialog("reassign")}>{t("requests.action_reassign")}</Button>}
        {can.clarify && (
          <Button onClick={() => setDialog("clarify")}><CircleHelp aria-hidden="true" className="size-4" />{t("requests.action_clarify")}</Button>
        )}
        {can.reject && (
          <Button onClick={() => setDialog("reject")}><XCircle aria-hidden="true" className="size-4" />{t("requests.action_reject")}</Button>
        )}
        {can.approve && (
          <Button variant="primary" onClick={() => setDialog("approve")}><Check aria-hidden="true" className="size-4" />{t("requests.action_approve")}</Button>
        )}
        {["APPROVED_FOR_STRATEGY", "STRATEGY_IN_PROGRESS"].includes(d.status) && (
          <Link to={`/projects/${d.display_id}/strategy`}
            className="inline-flex h-10 items-center gap-2 rounded-[var(--radius-control)] bg-[image:var(--gradient-primary-button)] px-4 text-sm font-medium text-[var(--text-on-accent)]">
            <Sparkles aria-hidden="true" className="size-4" />{t("requests.open_in_karya")}
          </Link>
        )}
      </div>
      <ApproveDialog d={d} open={dialog === "approve"} onClose={() => setDialog(null)} onDone={refresh} />
      <ClarifyDialog d={d} open={dialog === "clarify"} onClose={() => setDialog(null)} onDone={refresh} />
      <RejectDialog d={d} open={dialog === "reject"} onClose={() => setDialog(null)} onDone={refresh} />
      <ReassignDialog d={d} open={dialog === "reassign"} onClose={() => setDialog(null)} onDone={refresh} />
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : "unknown";
  return <p role="alert" className="mt-3 text-[14px] text-[var(--status-danger)]">{t(`error.${code}`, { defaultValue: t("error.unknown") })}</p>;
}

type DialogProps = { d: Detail; open: boolean; onClose: () => void; onDone: () => void };

function ApproveDialog({ d, open, onClose, onDone }: DialogProps) {
  const { t } = useTranslation();
  const m = useMutation({
    mutationFn: () => call(api.POST("/app/v1/requests/{display_id}/approve", { params: { path: { display_id: d.display_id } } })),
    onSuccess: () => { onDone(); onClose(); },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title={t("requests.approve_title")} description={t("requests.approve_body")}>
      <ErrorLine error={m.error} />
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="primary" onClick={() => m.mutate()} disabled={m.isPending}>{t("requests.action_approve")}</Button>
      </div>
    </Dialog>
  );
}

function ClarifyDialog({ d, open, onClose, onDone }: DialogProps) {
  const { t } = useTranslation();
  const [reasons, setReasons] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const m = useMutation({
    mutationFn: () => call(api.POST("/app/v1/requests/{display_id}/clarifications", {
      params: { path: { display_id: d.display_id } }, body: { reason_codes: reasons, message } })),
    onSuccess: () => { onDone(); onClose(); setReasons([]); setMessage(""); },
  });
  const valid = reasons.length > 0 && message.trim().length > 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title={t("requests.clarify_title")} description={t("requests.clarify_body")} wide>
      <fieldset>
        <legend className="mb-2 text-[13px] font-medium text-ink-2">{t("requests.clarify_reasons")}</legend>
        <div className="grid grid-cols-2 gap-2">
          {CLARIFY_REASONS.map((r) => (
            <label key={r} className="flex items-center gap-2 rounded-[var(--radius-control)] border border-line px-3 py-2 text-[14px] text-ink">
              <input type="checkbox" checked={reasons.includes(r)}
                onChange={(e) => setReasons(e.target.checked ? [...reasons, r] : reasons.filter((x) => x !== r))} />
              {t(`reason.clarify_${r.toLowerCase()}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <label htmlFor="clarify-msg" className="mb-2 mt-4 block text-[13px] font-medium text-ink-2">{t("requests.clarify_message")}</label>
      <textarea id="clarify-msg" rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
        className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink" />
      <ErrorLine error={m.error} />
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="primary" disabled={!valid || m.isPending} onClick={() => m.mutate()}>{t("requests.send_to_marketer")}</Button>
      </div>
    </Dialog>
  );
}

function RejectDialog({ d, open, onClose, onDone }: DialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [explanation, setExplanation] = useState("");
  const m = useMutation({
    mutationFn: () => call(api.POST("/app/v1/requests/{display_id}/reject", {
      params: { path: { display_id: d.display_id } }, body: { reason_code: reason, explanation } })),
    onSuccess: () => { onDone(); onClose(); },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title={t("requests.reject_title")} description={t("requests.reject_body")}>
      <label htmlFor="reject-reason" className="mb-2 block text-[13px] font-medium text-ink-2">{t("requests.reject_reason")}</label>
      <select id="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)}
        className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink">
        <option value="">{t("requests.choose_reason")}</option>
        {REJECT_REASONS.map((r) => <option key={r} value={r}>{t(`reason.reject_${r.toLowerCase()}`)}</option>)}
      </select>
      <label htmlFor="reject-exp" className="mb-2 mt-4 block text-[13px] font-medium text-ink-2">{t("requests.reject_explanation")}</label>
      <textarea id="reject-exp" rows={4} value={explanation} onChange={(e) => setExplanation(e.target.value)}
        className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink" />
      <p className="mt-3 text-[13px] text-ink-2">{t("requests.reject_notice")}</p>
      <ErrorLine error={m.error} />
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="primary" disabled={!reason || !explanation.trim() || m.isPending} onClick={() => m.mutate()}>
          {t("requests.reject_confirm")}
        </Button>
      </div>
    </Dialog>
  );
}

function ReassignDialog({ d, open, onClose, onDone }: DialogProps) {
  const { t } = useTranslation();
  const me = useSession();
  const [cp, setCp] = useState("");
  const cps = useQuery({ queryKey: ["creative-producers"], enabled: open && Boolean(me.permissions["request.reassign"]),
    queryFn: () => call<Person[]>(api.GET("/app/v1/creative-producers")) });
  const m = useMutation({
    mutationFn: () => call(api.POST("/app/v1/requests/{display_id}/reassign", {
      params: { path: { display_id: d.display_id } }, body: { cp_id: cp } })),
    onSuccess: () => { onDone(); onClose(); },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title={t("requests.reassign_title")}>
      {cps.data ? (
        <>
          <label htmlFor="reassign-cp" className="mb-2 block text-[13px] font-medium text-ink-2">{t("requests.reassign_to")}</label>
          <select id="reassign-cp" value={cp} onChange={(e) => setCp(e.target.value)}
            className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink">
            <option value="">{t("requests.choose_cp")}</option>
            {cps.data.filter((c) => c.id !== d.assigned_cp?.id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </>
      ) : (
        <p className="text-[14px] text-ink-2">{t("requests.reassign_ask_lead")}</p>
      )}
      <ErrorLine error={m.error} />
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="primary" disabled={!cp || m.isPending} onClick={() => m.mutate()}>{t("requests.action_reassign")}</Button>
      </div>
    </Dialog>
  );
}

function Field({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? "md:col-span-2" : undefined}>
      <dt className="text-[12px] uppercase tracking-wide text-ink-2">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-[15px] text-ink">{children}</dd>
    </div>
  );
}

function Brief({ d }: { d: Detail }) {
  const { t } = useTranslation();
  const b = d.brief;
  const empty = <span className="text-ink-2">{t("requests.not_provided")}</span>;
  return (
    <Card className="p-6">
      <h2 className="flex items-center gap-2 text-[18px] font-semibold text-ink"><BookOpen aria-hidden="true" className="size-5 text-violet" />{t("requests.marketer_brief")}</h2>
      <dl className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label={t("requests.col_product")}>{d.product.name}</Field>
        <Field label={t("requests.campaign")}>{d.campaign_id ?? empty}</Field>
        <Field label={t("requests.col_objective")}>{t(`requests.objective_${b.objective.toLowerCase()}`)}</Field>
        <Field label={t("requests.col_priority")}>{t(`requests.priority_${d.priority.toLowerCase()}`)}</Field>
        <Field wide label={t("requests.audience")}>{b.target_audience}</Field>
        <Field wide label={t("requests.insight")}>{b.marketing_insight ?? empty}</Field>
        <Field label={t("requests.angle")}>{b.requested_angle ?? empty}</Field>
        <Field label={t("requests.offer")}>{b.offer ?? empty}</Field>
        <Field label={t("requests.cta")}>{b.cta ?? empty}</Field>
        <Field label={t("requests.external_ref")}>{d.external_request_id}</Field>
      </dl>
      <h3 className="mt-6 text-[14px] font-semibold text-ink">{t("requests.col_deliverables")}</h3>
      <ul className="mt-2 flex flex-wrap gap-2">
        {d.deliverables.map((x, i) => (
          <li key={i} className="rounded-full border border-line bg-elevated px-3 py-1 text-[13px] text-ink">
            {t(`requests.deliverable_n_${x.type.toLowerCase()}`, { count: x.quantity })}{x.preferred_format ? ` · ${x.preferred_format}` : ""}
          </li>
        ))}
      </ul>
      {Object.keys(b.commercial_context).length > 0 && (
        <>
          <h3 className="mt-6 text-[14px] font-semibold text-ink">{t("requests.commercial_context")}</h3>
          <dl className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
            {Object.entries(b.commercial_context).map(([k, v]) => (
              <Field key={k} label={t(`requests.cc_${k}`, { defaultValue: k })}>{String(v)}</Field>
            ))}
          </dl>
        </>
      )}
      {b.references.length > 0 && (
        <>
          <h3 className="mt-6 text-[14px] font-semibold text-ink">{t("requests.references")}</h3>
          <ul className="mt-2 space-y-1 text-[14px]">
            {b.references.map((r, i) => (
              <li key={i}><a className="text-violet-text underline" href={String(r.value)} target="_blank" rel="noreferrer noopener">{String(r.label ?? r.value)}</a></li>
            ))}
          </ul>
        </>
      )}
      {d.resubmission_of && (
        <p className="mt-6 text-[14px] text-ink-2">{t("requests.resubmission_of")} <Link className="text-violet-text underline" to={`/requests/${d.resubmission_of}`}>{d.resubmission_of}</Link></p>
      )}
    </Card>
  );
}

function Clarifications({ d }: { d: Detail }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  if (d.clarifications.length === 0) return null;
  return (
    <Card className="p-6">
      <h2 className="text-[18px] font-semibold text-ink">{t("requests.clarifications")}</h2>
      <ol className="mt-4 space-y-4">
        {d.clarifications.map((c) => (
          <li key={c.id} className="rounded-[var(--radius-card)] border border-line p-4">
            <div className="flex items-center justify-between text-[13px] text-ink-2">
              <span>{t("requests.round_n", { n: c.round })} · {t(`status.clarification.${c.status.toLowerCase()}`)}</span>
              {c.requested_at && <span>{formatDateTime(new Date(c.requested_at), i18n.language, me.time_zone)}</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {c.reason_codes.map((r) => (
                <span key={r} className="rounded-full bg-[var(--status-warning-soft)] px-2.5 py-0.5 text-[12px] text-[var(--status-warning)]">{t(`reason.clarify_${r.toLowerCase()}`)}</span>
              ))}
            </div>
            <p className="mt-3 whitespace-pre-wrap text-[14px] text-ink">{c.message}</p>
            {c.response_text && (
              <div className="mt-3 rounded-[var(--radius-control)] bg-elevated p-3 text-[14px] text-ink">
                <div className="mb-1 text-[12px] uppercase tracking-wide text-ink-2">{t("requests.marketer_response")}</div>
                <p className="whitespace-pre-wrap">{c.response_text}</p>
              </div>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function RejectionCard({ d }: { d: Detail }) {
  const { t } = useTranslation();
  if (!d.rejection) return null;
  return (
    <Card className="border-[var(--status-danger)] p-6">
      <h2 className="flex items-center gap-2 text-[18px] font-semibold text-ink"><XCircle aria-hidden="true" className="size-5 text-[var(--status-danger)]" />{t("requests.rejected_title")}</h2>
      <p className="mt-2 text-[14px] font-medium text-ink">{t(`reason.reject_${d.rejection.reason_code.toLowerCase()}`)}</p>
      <p className="mt-2 whitespace-pre-wrap text-[14px] text-ink-2">{d.rejection.explanation}</p>
      {d.resubmitted_as && (
        <p className="mt-3 text-[14px]">{t("requests.resubmitted_as")} <Link className="text-violet-text underline" to={`/requests/${d.resubmitted_as}`}>{d.resubmitted_as}</Link></p>
      )}
    </Card>
  );
}

function Intelligence({ d }: { d: Detail }) {
  const { t } = useTranslation();
  const i = d.intelligence;
  const row = (icon: ReactNode, title: string, body: ReactNode) => (
    <li className="flex gap-3 py-3">
      <span aria-hidden="true" className="mt-0.5">{icon}</span>
      <div className="min-w-0"><div className="text-[14px] font-medium text-ink">{title}</div><div className="mt-0.5 text-[13px] text-ink-2">{body}</div></div>
    </li>
  );
  const missing = (i.brief_completeness?.missing as string[] | undefined) ?? [];
  return (
    <aside>
      <Card className="p-6">
        <h2 className="flex items-center gap-2 text-[16px] font-semibold text-ink"><Lightbulb aria-hidden="true" className="size-5 text-cyan" />{t("requests.intelligence")}</h2>
        <p className="mt-1 text-[12px] text-ink-2">{t("requests.intelligence_note")}</p>
        <ul className="mt-2 divide-y divide-[var(--border-default)]">
          {row(missing.length === 0 ? <Check className="size-4 text-[var(--status-success)]" /> : <CircleHelp className="size-4 text-[var(--status-warning)]" />, t("requests.intel_completeness"),
            missing.length === 0 ? t("requests.intel_complete") : t("requests.intel_missing", { fields: missing.map((f) => t(`requests.field_${f}`)).join(", ") }))}
          {row(<BookOpen className="size-4 text-violet" />, t("requests.intel_product_knowledge"),
            i.product_knowledge?.state === "available"
              ? t("requests.intel_facts", { count: Number(i.product_knowledge.facts ?? 0) }) + (i.product_knowledge.is_fixture ? ` · ${t("common.fixture_data")}` : "")
              : t("honest.insufficient_data"))}
          {row(<ShieldAlert className="size-4 text-ink-2" />, t("requests.intel_claims"), t("honest.capability_not_enabled"))}
          {row(<Sparkles className="size-4 text-magenta" />, t("requests.intel_similar"),
            t("requests.intel_similar_n", { count: Number(i.similar_recent?.count ?? 0), days: Number(i.similar_recent?.window_days ?? 90) }))}
          {row(<Sparkles className="size-4 text-magenta" />, t("requests.intel_angle"),
            i.angle_usage?.state === "available" ? t("requests.intel_angle_n", { count: Number(i.angle_usage.count ?? 0) }) : t("honest.insufficient_data"))}
          {row(<TriangleAlert className="size-4 text-[var(--status-warning)]" />, t("requests.intel_fatigue"), t("honest.insufficient_data"))}
        </ul>
      </Card>
    </aside>
  );
}
